/**
 * useOoReports — OO «Дашборд» приёмка отчётов об играх за день.
 *
 * One game = one summary (OoGameSummary): all bookings of the day joined with
 * their assignments (crew) + submitted game-reports. Mirrors the v3
 * ReportsPanel.load join (bookings + assignments + game-reports + quests +
 * staff) but maps the raw snake_case wire shape into typed camelCase the same
 * way useOperatorReports does, with an AbortController and a reload trigger.
 *
 * Mutations (accept / requestCorrection / savePrices / archive) hit the real
 * endpoints in apps/api/src/routes/game-reports.ts and reload on success.
 *
 * 02.09 (владелец): mock-fallback УДАЛЁН — пустой день показывает честное
 * пустое состояние, упавший фетч — ошибку; никаких выдуманных игр.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { ApiList } from "../api/types";
import { fmtDateApi, addDays, isNightTail } from "../utils/date";
import { isMirKvSource } from "../utils/mirkv"; import { waiverRequired } from "@ub/shared/waiver"; import { DEFAULT_GAME_RATE } from "@ub/shared/rates";
import { fetchDayExtras, type ExtrasMaps, type OoFan, type OoMaterial, type OoReportPlayer, type OoWaiverPerson, type OoPeerText, type OoWaiverPhoto, type OoMcOp, type OoMcNeed, type OoMcPrice } from "./ooDayExtras";
export type { OoMcOp, OoMcNeed } from "./ooDayExtras";
export type { OoReportPlayer, OoWaiverPerson, OoPeerText } from "./ooDayExtras";
import { buildDayBrief, type OoDayBrief } from "./ooDayBrief";
export type { OoDayBrief } from "./ooDayBrief";
export type { OoFan, OoMaterial } from "./ooDayExtras";

/* ── Typed shapes ─────────────────────────────────────────────────────────── */

export interface OoReport {
  id: number;
  bookingId: number;
  operatorId: string;
  operatorName: string;
  isPrimary: boolean;
  actualPlayers: number | null;
  /** Способы оплаты */
  cash: number;
  terminal: number;
  transfer: number;
  prepaidUsed: number;
  invoice: number;
  changeAmount: number;
  total: number;
  expectedTotal: number;
  /** Цены (правит OO) */
  questPrice: number;
  teaPrice: number; teaGift: boolean; /* 01.10: «чайная в подарок» (ОО) — 0 ₽ намеренно */
  mcPrice: number;
  nightSurcharge: number;
  taxiAmount: number;
  activity: string;
  comment: string;
  ooComment: string;
  accepted: boolean;
  needsCorrection: boolean;
  correctionReason: string;
  teaRoomId: string | null;
  /** МирКв-исход отчёта: "score" (5.0) | "cancel" (✕) | "" — символы формы оператора. */
  mkStatus: string; noWaiver: string | null; /* 01.10: «вейвера нет» — причина (null — отметки нет) */
}

/**
 * Одна строка состава с выплатой (как карточка отчёта V1): merge назначения и
 * сданного отчёта ПО ОПЕРАТОРУ. Ставка (`ratePaid`) берётся из назначения и
 * показывается всегда — даже если оператор отчёт ещё не сдал.
 */
export interface OoOperatorRow {
  operatorId: string;
  name: string;
  nickname: string | null;
  ratePaid: number; assignmentId: number | null; rateStd: number; /* + id назначения и сеточная ставка — редактор ставки (24.08) */
  taxi: number;
  /** id отчёта этого оператора — правка такси из чипа (27.08) */
  reportId: number | null;
  isPrimary: boolean;
  hasReport: boolean;
  /** 28.08: черновик сохранён, но отчёт не отправлен — пунктирный чип. */
  draftSaved: boolean;
  comment: string;
  activity: string;
  /** Его личная МирКв-отметка из отчёта: "score" | "cancel" | "". */
  mkStatus: string;
}

/** Peer-review строка («отчёт об операторе от оператора»). */
export interface OoPeer {
  authorName: string;
  /** null → автор пишет «о ком угодно» (subject не задан). */
  subjectName: string | null;
}

export interface OoGameSummary {
  id: string;
  bookingId: number;
  time: string;
  questShort: string;
  questName: string;
  questId: string;
  players: number;
  /** 08.09 (владелец): фактически пришло — из отчёта оператора (первичного); null — отчёта нет. */
  actualPlayers: number | null;
  branchId: string;
  operators: string[];
  /** Строки состава с выплатой (V1-форма карточки). */
  operatorRows: OoOperatorRow[];
  reports: OoReport[];
  /** Peer-review: «кто о ком пишет отчёт» для этой игры. */
  peers: OoPeer[];
  clientName: string | null;
  dcComment: string | null;
  source: string | null;
  tea: boolean;
  teaRoomId: string | null;
  /** Источник «Мир Квестов» (A14) — белая рамка + исход из отчёта. */
  isMirKv: boolean;
  /** Исход для МирКв-игры (Оценка / Отмена / Нет ответа / Без отчёта). */
  mkStatus: string | null;
  /** Доп-материалы и Unifans-игроки игры (23.08, Саша) — с превью. */
  materials: OoMaterial[]; unifans: OoFan[];
  /** 27.08 (владелец): исчерпывающая карточка — игроки из отчёта, вейвер-данные,
      тексты отзывов операторов друг о друге. */
  reportPlayers: OoReportPlayer[]; waiverPersons: OoWaiverPerson[]; peerTexts: OoPeerText[]; waiverPhotos: OoWaiverPhoto[]; noWaiver: string | null;
  /** Ожидаемая выручка игры из брони (28.08: сводная «собрано/ожидается»). */
  expectedRub: number;
  /** Поздняя отмена (28.08): операторы пришли, игроки нет — игра остаётся в дашборде. */
  cancelled: boolean; cancelReason: string;
  /** МК/допуслуги игры (28.08): кто вёл и ставка — чипы как у операторов. */
  mcOps: OoMcOp[];
  /** 19.09 (владелец): услуга в брони есть, оператор на неё не назначен — чип «назначить». */
  mcNeed: OoMcNeed[];
  /** 26.09 (владелец): прайс услуг брони — «Ожидалось» видит МК, добавленные после сдачи отчёта. */
  mcPrices: OoMcPrice[];
  /** Задаток по БРОНИ (28.08, владелец): виден в карточке даже если оператор
      не зачёл его в отчёте. */
  bookingPrepaid: number; /** 13.10 (владелец): учебная игра всегда бесплатна — ни ожидаемых денег, ни подсказок «подставить» */ isLearning?: boolean;
}

export interface OoReportEdit {
  id: number;
  field: string;
  oldValue: string;
  newValue: string;
  editorName: string;
  createdAt: string;
}

export interface OoDayCounts { total: number; done: number; disc: number; none: number; corr: number }

export interface PricesPatch { tea_gift?: number; quest_price?: number; tea_price?: number; mc_price?: number; night_surcharge?: number; oo_comment?: string; taxi_amount?: number }

export interface UseOoReportsResult {
  games: OoGameSummary[];
  counts: OoDayCounts;
  dayBrief: OoDayBrief; setDayBrief: (fn: (b: OoDayBrief) => OoDayBrief) => void; // «Брони дня»: сколько игр, ориентировочная сумма, проблемы контактов (24.08); setDayBrief — 11.10, админы зала из дашборда
  loading: boolean;
  error: string | null;
  reload: () => void;
  accept: (reportId: number) => Promise<void>;
  requestCorrection: (reportId: number, reason: string, operatorIds: string[]) => Promise<void>;
  savePrices: (reportId: number, patch: PricesPatch) => Promise<void>;
  archive: (bookingId: number) => Promise<void>;
  fetchEdits: (bookingId: number) => Promise<OoReportEdit[]>;
}

/* ── Raw wire shapes ──────────────────────────────────────────────────────── */

interface RawBooking {
  id: string | number;
  date?: string;
  quest_id?: string;
  quest_short?: string;
  short_name?: string;
  quest_name?: string;
  /** Название спецверсии — именно оно называет игру (22.09, владелец). */
  special_version_title?: string | null;
  time?: string;
  custom_time?: string | null;
  players?: number;
  branch_id?: string;
  client_name?: string | null;
  comment?: string | null;
  source?: string | null;
  tea?: number | boolean;
  tea_room_id?: string | null;
  type?: string;
  status?: string;
  archived?: number;
  expected_revenue?: number | null; is_learning?: number | boolean | null;
  prepaid_amount?: number | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
}

interface RawAssign {
  id?: number | string; booking_id: string | number;
  operator_id?: string;
  operator_name?: string;
  operator_nickname?: string | null;
  rate_paid?: number | null;
  is_primary?: number | boolean;
  status?: string;
}

/** GET /api/peer-review/requests?date= — пары author→subject за день. */
interface RawPeer {
  booking_id: number | string;
  author_id: string;
  subject_id?: string | null;
  author_name?: string | null;
  subject_name?: string | null;
}

interface RawReport {
  id: number;
  booking_id: number;
  operator_id?: string;
  operator_name?: string;
  is_primary?: number | boolean;
  actual_players?: number | null;
  cash?: number;
  terminal?: number;
  transfer?: number;
  prepaid_used?: number;
  invoice?: number;
  change_amount?: number;
  total?: number;
  expected_total?: number;
  quest_price?: number;
  tea_price?: number;
  mc_price?: number;
  night_surcharge?: number;
  taxi_amount?: number;
  activity?: string;
  comment?: string;
  oo_comment?: string;
  accepted?: number | boolean;
  accepted_by?: string | null;
  needs_correction?: number | boolean;
  correction_reason?: string;
  tea_room_id?: string | null;
  booking_tea_room_id?: string | null;
  mk_status?: string | null; tea_gift?: number | null; no_waiver?: number | null; no_waiver_reason?: string | null;
  source?: string | null;
}

interface RawQuest {
  id: string;
  name?: string;
  short_name?: string;
  branch_id?: string;
}

interface RawStaff {
  id: string;
  name?: string;
}

interface RawEdit {
  id: number;
  field: string;
  old_value?: string;
  new_value?: string;
  editor_name?: string;
  edited_by?: string;
  created_at?: string;
}

/* ── Mappers ──────────────────────────────────────────────────────────────── */

function mapReport(r: RawReport): OoReport {
  // accepted: the wire returns accepted_by (string|null), not a boolean column.
  const accepted = r.accepted != null ? !!r.accepted : !!r.accepted_by;
  return {
    id: r.id,
    bookingId: r.booking_id,
    operatorId: String(r.operator_id ?? ""),
    operatorName: r.operator_name ?? String(r.operator_id ?? "—"),
    isPrimary: r.is_primary == null ? true : !!r.is_primary,
    actualPlayers: r.actual_players == null ? null : Number(r.actual_players),
    cash: Number(r.cash ?? 0),
    terminal: Number(r.terminal ?? 0),
    transfer: Number(r.transfer ?? 0),
    prepaidUsed: Number(r.prepaid_used ?? 0),
    invoice: Number(r.invoice ?? 0),
    changeAmount: Number(r.change_amount ?? 0),
    total: Number(r.total ?? 0),
    expectedTotal: Number(r.expected_total ?? 0),
    questPrice: Number(r.quest_price ?? 0),
    teaPrice: Number(r.tea_price ?? 0), teaGift: !!Number(r.tea_gift),
    mcPrice: Number(r.mc_price ?? 0),
    nightSurcharge: Number(r.night_surcharge ?? 0),
    taxiAmount: Number(r.taxi_amount ?? 0),
    activity: r.activity ?? "",
    comment: r.comment ?? "",
    ooComment: r.oo_comment ?? "",
    accepted,
    needsCorrection: !!r.needs_correction,
    correctionReason: r.correction_reason ?? "",
    teaRoomId: r.tea_room_id ?? r.booking_tea_room_id ?? null,
    mkStatus: r.mk_status ?? "", noWaiver: Number(r.no_waiver) ? (r.no_waiver_reason || "причина не указана") : null,
  };
}

/** Tea-room presence from booking flag/room id. */
function bookingHasTea(b: RawBooking): boolean {
  return !!b.tea || !!b.tea_room_id;
}

/**
 * Состав игры с выплатой (V1-форма). Ключ — operator_id: назначение даёт
 * `ratePaid`/имя/ник, сданный отчёт даёт taxi/comment/activity/is_primary и
 * флаг hasReport. Оператор без отчёта остаётся в составе со своей ставкой.
 * Если назначений нет вовсе (staging без assignments), достраиваем
 * строки из самих отчётов, чтобы выплата хотя бы показалась по сдавшим.
 */
function buildOperatorRows(
  assigns: RawAssign[],
  reps: OoReport[],
  staffMap: Map<string, string>,
  rateOf: (opId: string) => number,
): OoOperatorRow[] {
  const byId = new Map<string, OoOperatorRow>();

  for (const a of assigns) {
    const oid = String(a.operator_id ?? "");
    if (!oid) continue;
    byId.set(oid, {
      operatorId: oid,
      name: a.operator_name ?? staffMap.get(oid) ?? oid,
      nickname: a.operator_nickname ?? null,
      /* Ставка из назначения; НЕТ ставки — из таблицы по умолчанию (23.08, Саша).
         12.09 (владелец: «не получается выставить зарплату 0»): ноль — это решение,
         а не пустое место. Раньше здесь стояло `|| rateOf(oid)`, и выставленный ноль
         на чипе подменялся сеточной ставкой: в базе и в расчёте ноль, а на экране
         прежние деньги — выглядело как «не сохранилось». */
      ratePaid: a.rate_paid == null ? rateOf(oid) : Number(a.rate_paid), assignmentId: a.id != null ? Number(a.id) : null, rateStd: rateOf(oid),
      taxi: 0,
      reportId: null,
      draftSaved: false,
      isPrimary: a.is_primary == null ? false : !!a.is_primary,
      hasReport: false,
      comment: "",
      activity: "",
      mkStatus: "",
    });
  }

  for (const r of reps) {
    const oid = r.operatorId || "";
    const existing = oid ? byId.get(oid) : undefined;
    if (existing) {
      existing.hasReport = true;
      existing.taxi = r.taxiAmount;
      existing.reportId = r.id;
      existing.comment = r.comment;
      existing.activity = r.activity;
      existing.mkStatus = r.mkStatus;
      existing.isPrimary = existing.isPrimary || r.isPrimary;
      if (existing.name === oid && r.operatorName) existing.name = r.operatorName;
    } else {
      // Отчёт без соответствующего назначения — заводим строку из отчёта.
      const key = oid || `r${r.id}`;
      byId.set(key, {
        operatorId: key,
        name: r.operatorName,
        nickname: null,
        ratePaid: rateOf(oid), assignmentId: null, rateStd: rateOf(oid),
        taxi: r.taxiAmount,
        reportId: r.id,
        draftSaved: false,
        isPrimary: r.isPrimary,
        hasReport: true,
        comment: r.comment,
        activity: r.activity,
        mkStatus: r.mkStatus,
      });
    }
  }

  // Первичный вперёд, затем по имени — детерминированный порядок.
  return [...byId.values()].sort(
    (a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.name.localeCompare(b.name, "ru"),
  );
}

/** Поздняя отмена (28.08, владелец): отменена не ранее чем за час до игры И
 *  операторы были выставлены — такие игры остаются в дашбордах, по ним сдают
 *  отчёты и платят ставки. cancelled_at в UTC, время игры местное (UTC+5). */
function isLateCancel(b: RawBooking, assignedIds: Set<string>): boolean {
  if (!b.cancelled_at || !assignedIds.has(String(b.id))) return false;
  const t = ((b.custom_time || b.time || "") as string).slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(t)) return false;
  const gameUtcMs = Date.parse(`${b.date ?? ""}T${t}:00Z`) - 5 * 3_600_000;
  const cancelMs = Date.parse(b.cancelled_at.replace(" ", "T") + (b.cancelled_at.includes("Z") ? "" : "Z"));
  return Number.isFinite(gameUtcMs) && Number.isFinite(cancelMs) && cancelMs >= gameUtcMs - 3_600_000;
}

function buildSummaries(
  bookings: RawBooking[],
  assigns: RawAssign[],
  reports: RawReport[],
  questMap: Map<string, RawQuest>,
  staffMap: Map<string, string>,
  peerMap: Map<string, OoPeer[]>,
  extras: ExtrasMaps,
  ratesByOp: Map<string, Record<string, number>>,
): OoGameSummary[] {
  const assignedIds = new Set(assigns.filter((a) => a.status !== "archived").map((a) => String(a.booking_id)));
  /* 06.09 (владелец): отменённая игра, по которой хоть кому-то начислена ставка, всегда в дашборде */
  const paidIds = new Set(assigns.filter((a) => a.status !== "archived" && Number(a.rate_paid ?? 0) > 0).map((a) => String(a.booking_id)));
  const games = bookings
    .filter((b) => (b.type ?? "booking") === "booking" && !b.archived
      && (b.status !== "cancelled" || paidIds.has(String(b.id)) || isLateCancel(b, assignedIds)))
    .map<OoGameSummary>((b) => {
      const bid = String(b.id);
      const q = b.quest_id ? questMap.get(b.quest_id) : undefined;
      const bookingAssigns = assigns.filter((a) => String(a.booking_id) === bid && a.status !== "archived");
      const ops = bookingAssigns
        .map((a) => a.operator_name ?? staffMap.get(String(a.operator_id)) ?? String(a.operator_id ?? "?"));
      const reps = reports
        .filter((r) => String(r.booking_id) === bid)
        .map(mapReport)
        // primary first so card.find(primary) is deterministic
        .sort((a, b2) => Number(b2.isPrimary) - Number(a.isPrimary));
      const qid = b.quest_id ?? "";
      const operatorRows = buildOperatorRows(bookingAssigns, reps, staffMap, (oid) => ratesByOp.get(oid)?.[qid] || DEFAULT_GAME_RATE);   // 13.10: без ставки пары — 350, как платит зарплата
      const draftSet = extras.drafts.get(bid);
      if (draftSet) for (const row of operatorRows) if (!row.hasReport && draftSet.has(row.operatorId)) row.draftSaved = true;
      const source = b.source ?? null;
      const isMirKv = isMirKvSource(source);
      /* МирКв-исход — mk_status первичного отчёта (символы формы оператора), не activity */
      const mkStatus = reps.find((r) => r.isPrimary)?.mkStatus || null;
      return {
        id: bid,
        bookingId: Number(b.id),
        time: (b.custom_time || b.time || "").slice(0, 5),
        questShort: b.special_version_title || b.quest_short || b.short_name || q?.short_name || q?.name || b.quest_name || "—",
        questName: b.special_version_title || q?.name || b.quest_name || b.quest_short || b.short_name || "—",
        questId: qid,
        players: Number(b.players ?? 0),
        /* reps отсортированы «первичный первым» — берём его факт, иначе первый заполненный */
        actualPlayers: reps.find((r) => r.actualPlayers != null)?.actualPlayers ?? null,
        branchId: b.branch_id ?? q?.branch_id ?? "",
        operators: ops,
        operatorRows,
        reports: reps,
        peers: peerMap.get(bid) ?? [],
        clientName: b.client_name ?? null,
        dcComment: b.comment ?? null,
        source,
        tea: bookingHasTea(b),
        teaRoomId: b.tea_room_id ?? null,
        isMirKv,
        mkStatus: isMirKv ? mkStatus : null,
        materials: extras.mats.get(bid) ?? [], unifans: extras.fans.get(bid) ?? [],
        reportPlayers: extras.rplayers.get(bid) ?? [], waiverPersons: extras.wpersons.get(bid) ?? [], peerTexts: extras.peertexts.get(bid) ?? [], waiverPhotos: extras.wphotos.get(bid) ?? [], noWaiver: (() => { const p = reps.find((r) => r.isPrimary); /* 01.10: фото удалили после сдачи — офис видит пробел */ return !p ? null : p.noWaiver ?? (waiverRequired(String(b.date ?? ""), true) && !(extras.wphotos.get(bid) ?? []).length ? "фото не приложено, причина не указана" : null); })(),
        expectedRub: b.is_learning ? Number(b.prepaid_amount) || 0 : Math.max(Number(b.expected_revenue) || 0, Number(b.prepaid_amount) || 0), isLearning: !!b.is_learning,
        cancelled: b.status === "cancelled",
        cancelReason: b.cancel_reason ?? "",
        mcOps: extras.mcops.get(bid) ?? [],
        mcNeed: extras.mcneed.get(bid) ?? [],
        mcPrices: extras.mcprice.get(bid) ?? [],
        bookingPrepaid: Number(b.prepaid_amount) || 0,
      };
    })
    .sort((a, b) => {
      /* ночной хвост (до 06:00) — в конец смены */
      const sk = (t: string) => (isNightTail(t) ? `${String(Number(t.slice(0, 2)) + 24)}:${t.slice(3)}` : t);
      return sk(a.time).localeCompare(sk(b.time));
    });
  return games;
}

/** booking_id → peer-review строки (из RawPeer списка). */
function buildPeerMap(peers: RawPeer[]): Map<string, OoPeer[]> {
  const map = new Map<string, OoPeer[]>();
  for (const p of peers) {
    const bid = String(p.booking_id);
    const arr = map.get(bid) ?? [];
    arr.push({
      authorName: p.author_name ?? String(p.author_id),
      subjectName: p.subject_name ?? (p.subject_id ? String(p.subject_id) : null),
    });
    map.set(bid, arr);
  }
  return map;
}

/** Day counters from summaries (matches the spec §A traffic-light segments). */
export function countDay(games: OoGameSummary[]): OoDayCounts {
  let done = 0;
  let disc = 0;
  let none = 0;
  let corr = 0;
  for (const g of games) {
    const primary = g.reports.find((r) => r.isPrimary) ?? g.reports[0];
    const hasReport = g.reports.length > 0;
    const diff = primary ? primary.total - primary.expectedTotal : 0;
    const hasDisc = !!primary && Math.abs(diff) >= 1;
    const needsCorr = !!primary && primary.needsCorrection;
    if (!hasReport) {
      if (!g.cancelled) none += 1; /* 06.09: отменённая игра с компенсацией — не «без отчёта» */
    } else if (needsCorr) {
      corr += 1;
    } else if (hasDisc && !primary.accepted) {
      disc += 1;
    } else {
      done += 1;
    }
  }
  return { total: games.length, done, disc, none, corr };
}


/* ── Hook ─────────────────────────────────────────────────────────────────── */

export function useOoReports(date: Date): UseOoReportsResult {
  const [games, setGames] = useState<OoGameSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<OoDayCounts>({ total: 0, done: 0, disc: 0, none: 0, corr: 0 }); const [dayBrief, setDayBrief] = useState<OoDayBrief>({ total: 0, revenue: 0, rows: [], extrawork: [], halladmins: [], dcactions: [] });
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    const d = fmtDateApi(date);
    const opt = { signal: ac.signal };

    (async () => {
      try {
        /* 27.08 (владелец): день дашборда включает ночной хвост D+1 (до 06:00) */
        const dNext = fmtDateApi(addDays(date, 1));
        const [bkR, asR, rpR, qR, stR, ppR, exR, rtR, bkN, asN, rpN, cxR] = await Promise.all([
          api<ApiList<RawBooking>>(`/api/bookings?date=${d}`, opt),
          api<ApiList<RawAssign>>(`/api/assignments?date=${d}`, opt).catch(() => ({ results: [] as RawAssign[] })),
          api<ApiList<RawReport>>(`/api/game-reports?date=${d}`, opt).catch(() => ({ results: [] as RawReport[] })),
          api<ApiList<RawQuest>>(`/api/quests`, opt).catch(() => ({ results: [] as RawQuest[] })),
          api<ApiList<RawStaff>>(`/api/staff?role=operator`, opt).catch(() => ({ results: [] as RawStaff[] })),
          api<ApiList<RawPeer>>(`/api/peer-review/requests?date=${d}`, opt).catch(() => ({ results: [] as RawPeer[] })),
          fetchDayExtras(d, opt),
          /* Таблица ставок «по умолчанию, кто сколько получает» (23.08, Саша) — фолбэк для чипов */
          api<{ rates?: { results?: { operator_id: string; quest_id: string; rate: number }[] } }>(`/api/rates/operators`, opt).catch(() => ({ rates: { results: [] } })),
          api<ApiList<RawBooking>>(`/api/bookings?date=${dNext}`, opt).catch(() => ({ results: [] as RawBooking[] })),
          api<ApiList<RawAssign>>(`/api/assignments?date=${dNext}`, opt).catch(() => ({ results: [] as RawAssign[] })),
          api<ApiList<RawReport>>(`/api/game-reports?date=${dNext}`, opt).catch(() => ({ results: [] as RawReport[] })),
          /* 06.09 (владелец): отменённые брони дня — /api/bookings отдаёт только active; нужны для игр с начислениями */
          api<ApiList<RawBooking>>(`/api/bookings/cancelled?from=${d}&to=${dNext}`, opt).catch(() => ({ results: [] as RawBooking[] })),
        ]);
        if (ac.signal.aborted || abortRef.current !== ac) return; // abortRef — эпоха: ответ уже не выбранного дня игнорируем

        /* склейка: свой день без ночных (они у вчерашней смены) + ночной хвост D+1 */
        const tOfB = (b: RawBooking) => ((b.custom_time || b.time || "") as string).slice(0, 5);
        const nightBk = (bkN.results ?? []).filter((b) => isNightTail(tOfB(b)));
        const nightIds = new Set(nightBk.map((b) => String(b.id)));
        const cxDay = (cxR.results ?? []).filter((b) => (b.date === d && !isNightTail(tOfB(b))) || (b.date === dNext && isNightTail(tOfB(b))));
        bkR.results = [...(bkR.results ?? []).filter((b) => !isNightTail(tOfB(b))), ...nightBk, ...cxDay];
        nightBk.forEach((b) => nightIds.add(String(b.id))); cxDay.filter((b) => b.date === dNext).forEach((b) => nightIds.add(String(b.id)));
        asR.results = [...(asR.results ?? []), ...(asN.results ?? []).filter((a) => nightIds.has(String(a.booking_id)))];
        rpR.results = [...(rpR.results ?? []), ...(rpN.results ?? []).filter((r) => nightIds.has(String(r.booking_id)))];

        const questMap = new Map<string, RawQuest>((qR.results ?? []).map((q) => [q.id, q]));
        const staffMap = new Map<string, string>((stR.results ?? []).map((s) => [s.id, s.name ?? s.id]));
        const peerMap = buildPeerMap(ppR.results ?? []);
        const ratesByOp = new Map<string, Record<string, number>>();
        for (const r of rtR.rates?.results ?? []) {
          const m = ratesByOp.get(String(r.operator_id)) ?? {};
          m[String(r.quest_id)] = Number(r.rate ?? 0);
          ratesByOp.set(String(r.operator_id), m);
        }
        const summaries = buildSummaries(
          bkR.results ?? [],
          asR.results ?? [],
          rpR.results ?? [],
          questMap,
          staffMap,
          peerMap,
          exR,
          ratesByOp,
        );

        setDayBrief(buildDayBrief(exR.created, exR.extrawork, exR.halladmins, exR.dcactions));
        setGames(summaries);
        setCounts(countDay(summaries));
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted || abortRef.current !== ac) return;
        setGames([]);
        setCounts(countDay([]));
        setError(e instanceof Error ? e.message : "Не удалось загрузить день");
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [date, trigger]);

  /* 28.08 (владелец): мутации БЕЗ перезагрузки страницы — точечный патч
     стейта; counts пересчитываются от обновлённых games. */
  const patchGames = useCallback((fn: (g: OoGameSummary) => OoGameSummary) => {
    setGames((gs) => { const next = gs.map(fn); setCounts(countDay(next)); return next; });
  }, []);

  const accept = useCallback(async (reportId: number) => {
    await api(`/api/game-reports/${reportId}/accept`, { method: "PUT" });
    patchGames((g) => g.reports.some((r) => r.id === reportId)
      ? { ...g, reports: g.reports.map((r) => (r.id === reportId ? { ...r, accepted: true } : r)) }
      : g);
  }, [patchGames]);

  const requestCorrection = useCallback(async (reportId: number, reason: string, operatorIds: string[]) => {
    await api(`/api/game-reports/${reportId}/request-correction`, {
      method: "POST",
      body: { reason, operator_ids: operatorIds },
    });
    const ops = new Set(operatorIds);
    patchGames((g) => g.reports.some((r) => r.id === reportId)
      ? { ...g, reports: g.reports.map((r) => (ops.has(r.operatorId) ? { ...r, needsCorrection: true, correctionReason: reason } : r)) }
      : g);
  }, [patchGames]);

  const savePrices = useCallback(async (reportId: number, patch: PricesPatch) => {
    await api(`/api/game-reports/${reportId}/prices`, { method: "PUT", body: patch });
    patchGames((g) => {
      if (!g.reports.some((r) => r.id === reportId)) return g;
      return { ...g, reports: g.reports.map((r) => {
        if (r.id !== reportId) return r;
        const n = { ...r };
        if (patch.quest_price !== undefined) n.questPrice = patch.quest_price;
        if (patch.tea_price !== undefined) n.teaPrice = patch.tea_price; if (patch.tea_gift !== undefined) { n.teaGift = !!patch.tea_gift; if (patch.tea_gift) n.teaPrice = 0; } else if ((patch.tea_price ?? 0) > 0) n.teaGift = false;
        if (patch.mc_price !== undefined) n.mcPrice = patch.mc_price;
        if (patch.night_surcharge !== undefined) n.nightSurcharge = patch.night_surcharge;
        if (patch.oo_comment !== undefined) n.ooComment = patch.oo_comment;
        if (patch.taxi_amount !== undefined) n.taxiAmount = patch.taxi_amount;
        n.expectedTotal = n.questPrice + n.teaPrice + n.mcPrice + n.nightSurcharge;
        return n;
      }), operatorRows: patch.taxi_amount !== undefined
        ? g.operatorRows.map((row) => (row.reportId === reportId ? { ...row, taxi: patch.taxi_amount! } : row))
        : g.operatorRows };
    });
  }, [patchGames]);

  const archive = useCallback(async (bookingId: number) => {
    await api("/api/game-reports/archive", { method: "PUT", body: { booking_id: bookingId } });
    setGames((gs) => { const next = gs.filter((g) => g.bookingId !== bookingId); setCounts(countDay(next)); return next; });
  }, []);

  const fetchEdits = useCallback(async (bookingId: number): Promise<OoReportEdit[]> => {
    try {
      const r = await api<ApiList<RawEdit>>(`/api/game-report-edits?booking_id=${bookingId}`);
      return (r.results ?? []).map((e) => ({
        id: e.id,
        field: e.field,
        oldValue: e.old_value ?? "",
        newValue: e.new_value ?? "",
        editorName: e.editor_name ?? e.edited_by ?? "",
        createdAt: e.created_at ?? "",
      }));
    } catch {
      return [];
    }
  }, []);

  return { games, counts, dayBrief, setDayBrief, loading, error, reload, accept, requestCorrection, savePrices, archive, fetchEdits };   /* setDayBrief — 11.10: правка админов зала из дашборда без перезагрузки экрана */
}
